/**
 * Hook com dados mockados para o Dash Companion
 * Módulo Motorista - Sistema IDOOH
 */

import { useState, useMemo, useCallback } from 'react';
import { CampaignMedia, MediaPack } from '../types/dashCompanion';

// Dados mockados
const mockMedias: CampaignMedia[] = [
    {
        id: 'MID-001',
        title: 'Promoção Natal Coca-Cola',
        thumbnailUrl: 'https://picsum.photos/300/200?random=1',
        mediaUrl: 'https://example.com/media1.mp4',
        type: 'image',
        startDate: new Date('2024-12-15'),
        endDate: new Date('2024-12-31'),
        status: 'active',
        estimatedEarnings: 35.00
    },
    {
        id: 'MID-002',
        title: 'Black Friday Extra',
        thumbnailUrl: 'https://picsum.photos/300/200?random=2',
        mediaUrl: 'https://example.com/media2.mp4',
        type: 'image',
        startDate: new Date('2024-11-20'),
        endDate: new Date('2024-11-30'),
        status: 'expired',
        estimatedEarnings: 25.00
    },
    {
        id: 'MID-003',
        title: 'Campanha Verão Havaianas',
        thumbnailUrl: 'https://picsum.photos/300/200?random=3',
        mediaUrl: 'https://example.com/media3.mp4',
        type: 'video',
        startDate: new Date('2024-12-20'),
        endDate: new Date('2025-02-28'),
        status: 'active',
        estimatedEarnings: 50.00
    },
    {
        id: 'MID-004',
        title: 'Ano Novo Amstel',
        thumbnailUrl: 'https://picsum.photos/300/200?random=4',
        mediaUrl: 'https://example.com/media4.mp4',
        type: 'video',
        startDate: new Date('2024-12-28'),
        endDate: new Date('2025-01-05'),
        status: 'pending',
        estimatedEarnings: 40.00
    },
    {
        id: 'MID-005',
        title: 'Volta às Aulas Kalunga',
        thumbnailUrl: 'https://picsum.photos/300/200?random=5',
        mediaUrl: 'https://example.com/media5.mp4',
        type: 'image',
        startDate: new Date('2025-01-10'),
        endDate: new Date('2025-02-15'),
        status: 'pending',
        estimatedEarnings: 30.00
    }
];

const mockPacks: MediaPack[] = [
    {
        id: 'PACK-001',
        name: 'Campanha Dezembro 2024',
        description: 'Pack promocional de fim de ano com grandes marcas',
        mediaCount: 3,
        totalEarnings: 125.00,
        medias: [mockMedias[0], mockMedias[2], mockMedias[3]],
        createdAt: new Date('2024-12-10')
    }
];

export function useDashCompanionData() {
    const [packs] = useState<MediaPack[]>(mockPacks);
    const [activeMedias, setActiveMedias] = useState<CampaignMedia[]>([mockMedias[0], mockMedias[2]]);
    const [selectedPack, setSelectedPack] = useState<MediaPack | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLoading] = useState(false);

    // Filtrar mídias por ID
    const filteredMedias = useMemo(() => {
        if (!searchQuery.trim()) return activeMedias;
        return activeMedias.filter(media =>
            media.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
            media.title.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [activeMedias, searchQuery]);

    // Remover mídia
    const removeMedia = useCallback((mediaId: string) => {
        setActiveMedias(prev => prev.filter(m => m.id !== mediaId));
    }, []);

    // Ativar pack (transmitir e monetizar)
    const activatePack = useCallback((pack: MediaPack) => {
        // Adicionar mídias do pack que ainda não estão ativas
        const newMedias = pack.medias.filter(
            packMedia => !activeMedias.some(active => active.id === packMedia.id)
        );

        if (newMedias.length > 0) {
            setActiveMedias(prev => [...prev, ...newMedias]);
        }

        setSelectedPack(null);
        return newMedias.length;
    }, [activeMedias]);

    // Abrir pack
    const openPack = useCallback((pack: MediaPack) => {
        setSelectedPack(pack);
    }, []);

    // Fechar pack
    const closePack = useCallback(() => {
        setSelectedPack(null);
    }, []);

    // Estatísticas
    const stats = useMemo(() => {
        const totalActive = activeMedias.filter(m => m.status === 'active').length;
        const totalEarnings = activeMedias.reduce((sum, m) => sum + (m.estimatedEarnings || 0), 0);
        const pendingCount = activeMedias.filter(m => m.status === 'pending').length;

        return {
            totalActive,
            totalEarnings,
            pendingCount,
            totalMedias: activeMedias.length
        };
    }, [activeMedias]);

    return {
        packs,
        activeMedias: filteredMedias,
        allActiveMedias: activeMedias,
        selectedPack,
        searchQuery,
        isLoading,
        stats,
        setSearchQuery,
        removeMedia,
        activatePack,
        openPack,
        closePack
    };
}
